import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { post } from 'axios';
import { Button, Dimmer, Dropdown, Icon, Label, Loader } from 'semantic-ui-react';
import { URL } from '../../../store/path';
import { setAlertModalState } from '../../common/data/actions';
import { fetchSegmap } from '../../segmap/data/actions';
import { getFilteredSegmap, getIsFetchingSegmap } from '../../segmap/data/selectors';


const MAX_SIZE = 500 * 1024;


const LogUploadForm = () => {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const [fileObj, setFileObj] = useState(null);
    const [fileName, setFileName] = useState('');
    const [isWaiting, setWaiting] = useState(false);
    const [resultMsg, setResultMsg] = useState(null);
    const [parentGroup, setParentGroup] = useState('');

    const segmapList = useSelector(getFilteredSegmap);
    const isFetchingSegmap = useSelector(getIsFetchingSegmap);

    useEffect(() => {
        dispatch(fetchSegmap());
    }, [dispatch]);

    // Unique parent groups from Segment Map
    const parentGroupOptions = useMemo(() => {
        const groups = [...new Set(
            segmapList
                .map(item => item && item.parent_group)
                .filter(Boolean)
        )].sort();
        return groups.map(group => ({ key: group, value: group, text: group }));
    }, [segmapList]);

    const showAlert = (type, title, msg) => {
        dispatch(setAlertModalState({
            isOpen: true,
            type,
            size: 'mini',
            title,
            msg,
        }));
    };

    const fileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        const ext = file.name.split('.').pop().toLowerCase();
        if (ext !== 'log') {
            showAlert('error', 'Invalid File', 'Only .log files are allowed to upload.');
            e.target.value = '';
            return;
        }
        if (file.size > MAX_SIZE) {
            showAlert('error', 'File Too Large', 'File size exceeds the maximum limit of 500KB.');
            e.target.value = '';
            return;
        }
        setFileObj(file);
        setFileName(file.name);
        setResultMsg(null);
    };

    const clearFile = () => {
        setFileObj(null);
        setFileName('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const fileUpload = () => {
        const formData = new FormData();
        formData.append('file', fileObj);
        formData.append('parentGroup', parentGroup);
        const config = {
            crossDomain: true,
            headers: {
                'X-RLB-DMS-ACC': localStorage.getItem('acc'),
                'Authorization': `Bearer ${localStorage.getItem('user')}`,
                'Content-Type': 'multipart/form-data',
            },
        };
        return post(URL + '/upload/log-upload', formData, config);
    };

    const onFormSubmit = async (e) => {
        e.preventDefault();
        if (!parentGroup) {
            showAlert('error', 'Parent Group Required', 'Please select a Parent Group.');
            return;
        }
        if (!fileObj) {
            showAlert('error', 'No File', 'Please select a log file to upload.');
            return;
        }
        setWaiting(true);
        try {
            const response = await fileUpload();
            const data = response.data || {};
            if (data.type === 'error') {
                setResultMsg({ type: 'error', text: `${fileName}: Uploaded with errors!` });
                showAlert('error', 'Upload Error', data.message || data.msg || data.data || 'Upload failed.');
            } else {
                setResultMsg({ type: 'success', text: `${fileName}: Successfully uploaded.` });
                showAlert('success', 'Success', data.message || data.msg || data.data || 'File uploaded successfully.');
                clearFile();
            }
        } catch (error) {
            const msg = error?.response?.data?.message
                || error?.response?.data?.msg
                || error?.response?.data?.data
                || error?.message
                || 'Upload failed.';
            setResultMsg({ type: 'error', text: `${fileName}: Upload failed.` });
            showAlert('error', 'Upload Error', msg);
        } finally {
            setWaiting(false);
        }
    };

    return (
        <>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 4px 12px',
            }}>
                <div>
                    <div style={{ fontSize: '17px', fontWeight: 600, color: '#1f2d3d', lineHeight: 1.2 }}>
                        Upload LOG Data
                    </div>
                    <div style={{ fontSize: '12px', color: '#8a96a8', marginTop: '2px' }}>
                        Upload a log file for processing (max 500KB).
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 16px' }}>
                <div style={{
                    width: '100%',
                    maxWidth: '520px',
                    background: '#ffffff',
                    border: '1px solid #eef1f5',
                    borderRadius: '12px',
                    padding: '28px 28px 22px',
                    boxShadow: '0 2px 10px rgba(33, 65, 112, 0.05)',
                    position: 'relative',
                }}>
                    {isWaiting && (
                        <Dimmer active inverted style={{ borderRadius: '12px' }}>
                            <Loader indeterminate>Processing File...</Loader>
                        </Dimmer>
                    )}

                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <div style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '14px',
                            background: '#e8f2fb',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '12px',
                        }}>
                            <Icon name='file alternate outline' style={{ color: '#2185d0', fontSize: '24px', margin: 0 }} />
                        </div>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#1f2d3d' }}>
                            Log File Upload
                        </div>
                        <div style={{ fontSize: '12.5px', color: '#8a96a8', marginTop: '4px' }}>
                            Only <strong>.log</strong> files up to <strong>500KB</strong> are supported.
                        </div>
                    </div>

                    <form onSubmit={onFormSubmit}>
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{
                                fontSize: '11.5px',
                                fontWeight: 600,
                                color: '#5a6b80',
                                textTransform: 'uppercase',
                                letterSpacing: '0.4px',
                                marginBottom: '6px',
                            }}>
                                Parent Group <span style={{ color: '#db2828' }}>*</span>
                            </div>
                            <Dropdown
                                placeholder='Select Parent Group'
                                fluid
                                search
                                selection
                                clearable
                                loading={isFetchingSegmap}
                                options={parentGroupOptions}
                                value={parentGroup}
                                onChange={(e, { value }) => setParentGroup(value)}
                                noResultsMessage='No parent group found.'
                                disabled={isWaiting}
                            />
                        </div>

                        <label
                            htmlFor='log-file'
                            style={{
                                display: 'block',
                                border: '1.5px dashed #b4d4f0',
                                borderRadius: '10px',
                                background: '#f7fbff',
                                padding: '20px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                transition: 'background 0.15s ease, border-color 0.15s ease',
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.background = '#eef4fb' }}
                            onMouseOut={(e) => { e.currentTarget.style.background = '#f7fbff' }}
                        >
                            <Icon name='cloud upload' style={{ color: '#2185d0', fontSize: '22px', marginBottom: '6px' }} />
                            <div style={{ fontSize: '13px', color: '#2185d0', fontWeight: 600 }}>
                                Click to choose a log file
                            </div>
                            <div style={{ fontSize: '11.5px', color: '#8a96a8', marginTop: '2px' }}>
                                or drag and drop here
                            </div>
                        </label>
                        <input
                            id='log-file'
                            ref={fileInputRef}
                            type='file'
                            accept='.log'
                            style={{ display: 'none' }}
                            onChange={fileChange}
                        />

                        {fileObj && (
                            <div style={{ marginTop: '14px', textAlign: 'center' }}>
                                <Label
                                    color='green'
                                    basic
                                    style={{
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <Icon name='file alternate' />
                                    <span title={fileName}>{fileName}</span>
                                    <Icon
                                        name='delete'
                                        link
                                        style={{ marginLeft: '6px' }}
                                        onClick={clearFile}
                                    />
                                </Label>
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                            <Button
                                type='button'
                                basic
                                fluid
                                onClick={clearFile}
                                disabled={!fileObj || isWaiting}
                            >
                                Clear
                            </Button>
                            <Button
                                type='submit'
                                color='blue'
                                fluid
                                icon
                                labelPosition='left'
                                disabled={!fileObj || !parentGroup || isWaiting}
                            >
                                <Icon name='upload' />
                                Upload
                            </Button>
                        </div>
                    </form>

                    {resultMsg && (
                        <div style={{
                            marginTop: '14px',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            background: resultMsg.type === 'success' ? '#e6f7eb' : '#fdecea',
                            color: resultMsg.type === 'success' ? '#1a7332' : '#c0392b',
                            border: `1px solid ${resultMsg.type === 'success' ? '#c4e8cf' : '#f5c6cb'}`,
                            fontSize: '12.5px',
                            textAlign: 'center',
                        }}>
                            <Icon name={resultMsg.type === 'success' ? 'check circle' : 'times circle'} />
                            {resultMsg.text}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default LogUploadForm;
